import usePrivateAxios from '@/common/api/usePrivateAxios'
import { getRailsUpdate } from '@/common/utils/getRailsUpdate'
import { mapToDeletion } from '@/common/utils/railsHelpers'
import { ProductValidationSchema } from '@/features/products/schemas/create.schema'
import { updateProductTefIdentifierValue } from '@/features/products/services/updateProductTefIdentifierValue'
import { ProductData } from '@/features/products/types/product'
import { useMutation } from '@tanstack/react-query'
import { AxiosError, AxiosResponse } from 'axios'

interface UseUpdateProductProps {
  productId: string
  product?: ProductData
  onSuccess?: (response: AxiosResponse) => void
  onError?: (error: AxiosError) => void
}

export const useUpdateProduct = ({ productId, product, onSuccess, onError }: UseUpdateProductProps) => {
  const privateAxios = usePrivateAxios()

  return useMutation({
    mutationKey: ['updateProduct'],
    mutationFn: async (form: ProductValidationSchema) => {
      const haveTefIdentifiers =
        form.product_tef_identifiers?.length &&
        form.product_tef_identifiers?.some(
          (tefIdentifier) => !!tefIdentifier.tef_provider?.value && !!tefIdentifier?.identifier,
        )

      const baseUpdatedProductTefIdentifiers = [
        ...getRailsUpdate(
          product?.product_tef_identifiers || [],
          form.product_tef_identifiers || [],
          (value) => value.id,
          (value) => value.identifier_id || '',
          mapToDeletion,
          (value) => ({ tef_provider: value.tef_provider?.value || 'fiserv', identifier: value.identifier }),
        ),
        ...updateProductTefIdentifierValue({ product, tefIdentifiers: form.product_tef_identifiers }),
      ]

      const hasInvalidIdentifiers = baseUpdatedProductTefIdentifiers.some(
        (identifier) => !identifier.identifier || identifier.identifier.trim() === '',
      )

      const updatedProductTefIdentifiers = hasInvalidIdentifiers
        ? baseUpdatedProductTefIdentifiers.map((identifier) => {
            const isInvalidIdentifier =
              !identifier.tef_provider || !identifier.identifier || identifier.identifier.trim() === ''
            return isInvalidIdentifier ? { ...identifier, _destroy: true } : identifier
          })
        : baseUpdatedProductTefIdentifiers

      const payload = {
        ...form,
        category_id: form.category.value,
        unit_for_quantity: form.unit_for_quantity.value,
        product_tef_identifiers_attributes: updatedProductTefIdentifiers,
        equipment_category_ids: haveTefIdentifiers
          ? form.equipment_category_ids?.map((equipmentCategory) => equipmentCategory.value)
          : [],
        fiscal_document_kind: form.fiscal_document_kind?.value || 'none',
      }

      delete payload.equipment_tier

      return await privateAxios.put(`/products/${productId}`, {
        product: payload,
      })
    },
    onSuccess,
    onError,
  })
}
